import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { server } from "../../../api";

// 直播狀態機，由 function3.js 的 RTMP 部分抽出來共用。
//
// 水情機具（function_11）與一般影像機具走同一組 /v1/streaming/* API（後端
// 的 resolver 會依序查 function_3、function_11），所以播放邏輯完全一樣，
// 沒有理由維護兩份 —— 那只會讓其中一份的 bug 永遠修不到。
//
// 狀態：idle | loading | active | unavailable
//
// 關鍵細節：RTMP 的 live 欄位來自 nginx /stat 的快取，會延遲而且會少報 ——
// 健康的串流常常回 live:"idle" 但 .m3u8 其實已經在供應。所以 playlist 探測
// 才是最終依據。

const RETRY_INTERVAL_S = 3;
const UNAVAILABLE_COPY = {
  disabled: "串流已關閉",
  idle: "未推流",
  error: "連線失敗",
};

async function probeHls(url) {
  try {
    await axios.get(url, { timeout: 3000,
                           validateStatus: (status) => status === 200 });
    return true;
  } catch (err) {
    return false;
  }
}

export { UNAVAILABLE_COPY };

export default function useStreamFeed(machineID, { open }) {
  const [status, setStatus] = useState("idle");
  const [hlsUrl, setHlsUrl] = useState(null);
  const [posterUrl, setPosterUrl] = useState(null);
  const [posterBust, setPosterBust] = useState(0);
  const [reason, setReason] = useState(null);
  const [retryIn, setRetryIn] = useState(null);
  const [viewers, setViewers] = useState(0);

  const sessionId = useRef(
    `water-${machineID}-${Math.random().toString(36).slice(2)}`,
  ).current;
  const mounted = useRef(true);
  const viewerStarted = useRef(false);
  const starting = useRef(false);

  // unmount 時釋放觀看者名額。沒有這段的話，關掉分頁會讓計數一直掛著，
  // 直到後端 TTL 過期為止。
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      if (starting.current || viewerStarted.current) {
        // unmount 中不能用 axios（可能被中斷），keepalive 的 fetch 才送得出去
        fetch(`${server}v1/streaming/viewer/stop/${machineID}`, {
          method: "POST",
          headers: { "X-Session-ID": sessionId },
          keepalive: true,
        }).catch(() => {});
      }
    };
  }, [machineID, sessionId]);

  const resolve = useCallback(async (data) => {
    if (!mounted.current) return;
    // 只在值真的改變時才 setState。健康檢查每 5 秒跑一次，無條件 setState
    // 會讓整棵元件重新 render —— 播放器的 options 物件跟著重建，video.js
    // 就會把播放器砍掉重蓋，畫面每 5 秒黑一次。
    if (data.last_frame_url) {
      setPosterUrl((prev) => (prev === data.last_frame_url ? prev
                                                           : data.last_frame_url));
    }
    setViewers((prev) => (prev === (data.viewers ?? 0) ? prev : data.viewers ?? 0));

    if (data.active === 0) {
      setReason("disabled");
      setStatus("unavailable");
      return;
    }
    if (data.live === "streaming" && data.hls_url) {
      setHlsUrl((prev) => (prev === data.hls_url ? prev : data.hls_url));
      setReason(null);
      setStatus("active");
      return;
    }
    // /stat 說沒在推流，但快取會延遲 —— 以 playlist 為準
    if (data.hls_url && (await probeHls(data.hls_url))) {
      if (!mounted.current) return;
      setHlsUrl((prev) => (prev === data.hls_url ? prev : data.hls_url));
      setReason(null);
      setStatus("active");
      return;
    }
    if (!mounted.current) return;
    setReason(data.live === "error" ? "error" : "idle");
    setStatus("unavailable");
  }, []);

  const start = useCallback(async () => {
    starting.current = true;
    setPosterBust((n) => n + 1);
    setStatus("loading");
    setReason(null);
    try {
      const response = await axios.post(
        `${server}v1/streaming/viewer/start/${machineID}`, {},
        { headers: { "X-Session-ID": sessionId } },
      );
      if (!mounted.current) return;
      viewerStarted.current = true;
      await resolve(response.data);
    } catch (error) {
      if (!mounted.current) return;
      setReason(error.response?.status === 403 ? "disabled" : "error");
      setStatus("unavailable");
    } finally {
      starting.current = false;
    }
  }, [machineID, sessionId, resolve]);

  // 開啟時啟動，關閉時釋放名額並重設
  useEffect(() => {
    if (open) {
      start();
      return undefined;
    }
    if (viewerStarted.current) {
      axios.post(`${server}v1/streaming/viewer/stop/${machineID}`, {},
                 { headers: { "X-Session-ID": sessionId } }).catch(() => {});
      viewerStarted.current = false;
    }
    setStatus("idle");
    setHlsUrl(null);
    setReason(null);
    setRetryIn(null);
    return undefined;
  }, [open, machineID, sessionId, start]);

  // 尚未播放時每 3 秒重試。RTMP 從開啟到 HLS 可用大約要 5–8 秒，這段倒數
  // 讓使用者看得出系統正在等，而不是壞掉了。
  useEffect(() => {
    if (!open || status === "active") {
      setRetryIn(null);
      return undefined;
    }
    let left = RETRY_INTERVAL_S;
    setRetryIn(left);
    const ticker = setInterval(async () => {
      left -= 1;
      if (left > 0) {
        setRetryIn(left);
        return;
      }
      left = RETRY_INTERVAL_S;
      setRetryIn(left);
      try {
        const { data } = await axios.get(`${server}v1/streaming/state/${machineID}`);
        await resolve(data);
      } catch (err) {
        /* 下一輪再試 */
      }
    }, 1000);
    return () => clearInterval(ticker);
  }, [open, status, machineID, resolve]);

  // 播放中的健康檢查。串流可能在播放器底下無聲死掉 —— guard 的閒置踢除與
  // 300 秒上限都會把 active 設為 0，而 video.js 不會因此觸發 error 事件，
  // 畫面就一直停在「串流中」配一張凍結的影格。
  useEffect(() => {
    if (!open || status !== "active") return undefined;
    let fails = 0;
    const id = setInterval(async () => {
      try {
        const { data } = await axios.get(`${server}v1/streaming/state/${machineID}`);
        if (!mounted.current) return;
        setViewers((prev) => (prev === (data.viewers ?? 0) ? prev
                                                           : data.viewers ?? 0));
        if (data.active === 0) {
          setReason("disabled");
          setStatus("unavailable");
          return;
        }
        if (data.live === "streaming") {
          fails = 0;
          return;
        }
        if (data.hls_url && (await probeHls(data.hls_url))) {
          fails = 0;
          return;
        }
        fails += 1;
        if (fails >= 2 && mounted.current) {   // 容忍一次暫時性失敗
          setReason("idle");
          setStatus("unavailable");
        }
      } catch (err) {
        /* 忽略 */
      }
    }, 5000);
    return () => clearInterval(id);
  }, [open, status, machineID]);

  return {
    status,
    hlsUrl,
    posterUrl: posterUrl ? `${posterUrl}?b=${posterBust}` : null,
    reason,
    reasonText: reason ? UNAVAILABLE_COPY[reason] : null,
    retryIn,
    viewers,
    refresh: start,
  };
}